import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useFormContext, useFieldArray } from 'react-hook-form';
import { getSupplierSelect } from '@/api/get/config/get-company-select';
import { statusOptions } from '@/common/options';
import Card from '@/components/card/card-component';
import { Col, Row } from '@/components/grid/grid-component';
import Loader from '@/components/loader/loader-component';
import ColorPicker from '@/components/react-hook-form-inputs/color-picker/color-picker-component';
import FileUploader from '@/components/react-hook-form-inputs/file-dropzone/file-dropzone-component';
import Input from '@/components/react-hook-form-inputs/input/input-component';
import PhoneInputComponent from '@/components/react-hook-form-inputs/input/phone-input/phone-input-component';
import RadioButtonGroup from '@/components/react-hook-form-inputs/radio-button/radio-button-component';
import SelectSearch from '@/components/react-hook-form-inputs/select-search/select-search-component';
import ToggleSwitch from '@/components/react-hook-form-inputs/toggle-switch/toggle-switch-component';
import { Heading3Text, Subtitle2Text } from '@/utils/global-fonts';
import {
  cellphoneNumberRegex,
  emailRegex,
  requiredValidationDropdownMessage,
  requiredValidationMessage,
  urlValidationRules,
} from '@/utils/validations';
import Styled from './company-form-style';

/**
 * Company form
 * @param { string }type - form type edit or create
 * @returns {JSX.Element}
 */
const CompanyForm = ({ type, isLoading: updateLoading }) => {
  const [companyStatus, setCompanyStatus] = useState('');

  const {
    formState: { isLoading: formLoading },
    watch,
    clearErrors,
    control,
    setValue,
    getValues,
  } = useFormContext();

  const isLoading = formLoading || updateLoading;
  const companyStatusInitialColor = watch('company.status');
  const dealer = watch('company.is_dealer');
  const supplier = watch('company.is_supplier');
  const terms = watch('whiteLabelling.terms_type');
  const whiteLabelling = watch('whiteLabelling');

  const [suppliersLoading, setSuppliersLoading] = useState(true);
  const [supplierOptions, setSupplierOptions] = useState([]);

  const { fields: selectedSuppliers, remove: removeSupplier } = useFieldArray({
    control,
    name: 'selectedSuppliers',
  });

  const handleSupplierChange = (newSelectedSuppliers) => {
    // Ensure newSelectedSuppliers is an array
    const suppliersArray = Array.isArray(newSelectedSuppliers)
      ? newSelectedSuppliers
      : [];

    // Clear existing suppliers and add new ones in one operation
    const newSuppliers = suppliersArray.map((supplierOption) => ({
      supplierId: supplierOption.value,
      supplierName: supplierOption.label,
      ueEndpoint: '',
    }));

    // Replace all suppliers at once
    setValue('selectedSuppliers', newSuppliers);

    // Update the suppliers field for SelectSearch
    const supplierIds = suppliersArray.map((sup) => sup.value);
    setValue('suppliers', supplierIds);
  };

  const handleRemoveSupplier = (index) => {
    // Remove from field array
    removeSupplier(index);

    // Update the SelectSearch value by removing the supplier from the suppliers field
    const currentSuppliers = getValues('suppliers') || [];
    const supplierToRemove = selectedSuppliers[index];
    const updatedSuppliers = currentSuppliers.filter(
      (supplierId) => supplierId !== supplierToRemove.supplierId,
    );
    setValue('suppliers', updatedSuppliers);
  };

  useEffect(() => {
    getSupplierSelect().then((response) => {
      const suppliers = response.data.map((d) => ({
        value: d.id,
        label: d.name,
      }));
      setSupplierOptions(suppliers);
      setSuppliersLoading(false);
    });
  }, []);

  // Update supplier names when options load
  useEffect(() => {
    if (supplierOptions.length > 0 && selectedSuppliers.length > 0) {
      const updatedSuppliers = selectedSuppliers.map((supplierField) => {
        const supplierData = supplierOptions.find(
          (opt) => opt.value === supplierField.supplierId,
        );
        return {
          ...supplierField,
          supplierName: supplierData?.label || supplierField.supplierName,
        };
      });

      // Only update if there are changes
      const hasChanges = updatedSuppliers.some(
        (supplierField, index) =>
          supplierField.supplierName !== selectedSuppliers[index]?.supplierName,
      );

      if (hasChanges) {
        setValue('selectedSuppliers', updatedSuppliers);
      }
    }
  }, [supplierOptions, selectedSuppliers, setValue]);

  const termsOptions = [
    { value: 'link', label: 'URL Link' },
    { value: 'text', label: 'Text' },
  ];

  if (isLoading) {
    return <Loader loading />;
  }
  const radioContainerStyle = { width: '50%' };
  const mainContainerStyle = {
    width: '50%',
    justifyContent: 'space-between',
    gap: '12px',
    marginLeft: 12,
  };

  const toggleSwitchStyle = {
    marginTop: '10px',
    marginLeft: '5px',
  };

  return (
    <>
      <Card padding="16px 18px 10px 24px">
        <Row>
          <Col xs={12}>
            <Heading3Text>
              {type === 'create' ? 'Create Company' : 'Edit Company'}
            </Heading3Text>
          </Col>
        </Row>
        <Row>
          <Col sm={12} md={12} lg={6} xl={6} xxl={6}>
            <Input
              disabled={isLoading}
              label="Company Name"
              placeholder="Company Name"
              name="company.name"
              registerOptions={{
                required: requiredValidationMessage,
              }}
            />
          </Col>
          <Col sm={12} md={12} lg={6} xl={6} xxl={6}>
            <Input
              disabled={isLoading}
              label="Registered Address"
              placeholder="Registered Address"
              name="company.address"
              registerOptions={{
                required: requiredValidationMessage,
              }}
            />
          </Col>
          <Col sm={12} md={12} lg={6} xl={6} xxl={6}>
            <Input
              disabled={isLoading}
              label="Registration Number"
              placeholder="Registration Number"
              name="company.registration_number"
              registerOptions={{
                required: requiredValidationMessage,
              }}
            />
          </Col>
          <Col sm={12} md={12} lg={6} xl={6} xxl={6}>
            <Input
              label="Email Address"
              disabled={isLoading}
              placeholder="Email Address"
              name="company.email"
              registerOptions={{
                validate: (value) => {
                  let errorMessage = null;
                  emailRegex.forEach((rule) => {
                    if (!rule.pattern.test(value)) {
                      errorMessage = rule.message;
                    }
                  });
                  return errorMessage || true;
                },
              }}
            />
          </Col>
          <Col sm={12} md={12} lg={6} xl={6} xxl={6}>
            <PhoneInputComponent
              name="company.contact_number"
              disabled={isLoading}
              registerOptions={{
                validate: (value) => {
                  // TODO:Add this back after QA.I have removed this for now so that the FE triggers a required error message to accomodate the BE.
                  // if (value && value !== undefined) {
                  let errorMessage = null;
                  cellphoneNumberRegex.forEach((rule) => {
                    if (!rule.pattern.test(value)) {
                      errorMessage = rule.message;
                    }
                  });
                  return errorMessage || true;
                },
              }}
            />
          </Col>
          <Col sm={12} md={12} lg={6} xl={6} xxl={6}>
            <SelectSearch
              cy="selCompanyStatus"
              header="Status"
              name="company.status"
              label="Please select a status"
              options={statusOptions}
              icon={
                <Styled.StatusCircle
                  status={companyStatusInitialColor || companyStatus}
                />
              }
              required={{
                required: requiredValidationDropdownMessage,
                onChange: (e) => {
                  setCompanyStatus(e.target.value);
                },
              }}
              isLoading={isLoading}
            />
          </Col>
          <Col sm={6} md={6} lg={6} xl={6} xxl={6}>
            <ToggleSwitch
              style={toggleSwitchStyle}
              label="Activate Company as a Dealer"
              name="company.is_dealer"
              disabled={isLoading}
              onChange={() => clearErrors('company.is_supplier')}
              registerOptions={{
                validate: () => {
                  if (
                    watch('company.is_supplier') ||
                    watch('company.is_dealer')
                  ) {
                    return true;
                  }
                  return 'Please select at least one option';
                },
              }}
            />

            {dealer && (
              <SelectSearch
                closeMenuOnSelect={false}
                cy="selCompanyStatus"
                header="Select Suppliers"
                name="suppliers"
                label="Please select a supplier"
                options={supplierOptions}
                isMulti
                maxMenuHeight={200}
                menuPlacement="bottom"
                hideSelectedOptions
                showCheckBox
                onChange={handleSupplierChange}
                required={{
                  required: 'Please select a supplier',
                  pattern: {
                    value: /^[a-zA-Z]+$/,
                    message: 'Field cannot be empty',
                  },
                }}
                isLoading={isLoading || suppliersLoading}
              />
            )}
          </Col>

          <Col sm={6} md={6} lg={6} xl={6} xxl={6}>
            <ToggleSwitch
              style={toggleSwitchStyle}
              label="Activate Company as a Supplier"
              name="company.is_supplier"
              disabled={isLoading}
              onChange={() => clearErrors('company.is_dealer')}
              registerOptions={{
                validate: () => {
                  if (
                    watch('company.is_supplier') ||
                    watch('company.is_dealer')
                  ) {
                    return true;
                  }
                  return 'Please select at least one option';
                },
              }}
            />
            {supplier && (
              <Input
                label="Unreal Endpoint"
                name="company.ue_endpoint"
                placeholder="ETTX Default"
                registerOptions={{
                  validate: (value) => {
                    if (!value || value.trim() === '') return true; // Allow empty value
                    let errorMessage = null;
                    urlValidationRules.forEach((rule) => {
                      if (!rule.pattern.test(value)) {
                        errorMessage = rule.message;
                      }
                    });
                    return errorMessage || true;
                  },
                }}
              />
            )}
          </Col>
          {dealer && (
            <Col sm={12} md={12} lg={12} xl={12} xxl={12}>
              {/* Dynamic Supplier Fields */}
              {selectedSuppliers.map((supplierField, index) => {
                const supplierData = supplierOptions.find(
                  (opt) => opt.value === supplierField.supplierId,
                );
                return (
                  <Styled.SupplierContainer
                    key={`supplier-container-${supplierField.supplierId}`}
                  >
                    <Col sm={6} md={6} lg={6} xl={6} xxl={6}>
                      <Input
                        disabled
                        label="Supplier Name"
                        value={supplierData?.label || ''}
                        name={`selectedSuppliers.${index}.supplierName`}
                      />
                    </Col>
                    <Col sm={6} md={6} lg={6} xl={6} xxl={6}>
                      <Styled.SupplierInputContainer>
                        <Input
                          label="Unreal Engine Integration"
                          placeholder="ETTX Default"
                          name={`selectedSuppliers.${index}.ueEndpoint`}
                          registerOptions={{
                            validate: (value) => {
                              if (!value || value.trim() === '') return true; // Allow empty value
                              let errorMessage = null;
                              urlValidationRules.forEach((rule) => {
                                if (!rule.pattern.test(value)) {
                                  errorMessage = rule.message;
                                }
                              });
                              return errorMessage || true;
                            },
                          }}
                        />

                        <Styled.RemoveSupplierIcon
                          onClick={() => handleRemoveSupplier(index)}
                        />
                      </Styled.SupplierInputContainer>
                    </Col>
                  </Styled.SupplierContainer>
                );
              })}
            </Col>
          )}
          {dealer && (
            <Col sm={6} md={6} lg={6} xl={6} xxl={6}>
              <ToggleSwitch
                style={toggleSwitchStyle}
                label="Give Company access to the Quoting Function"
                name="company.quoting_function_access"
                disabled={isLoading}
                onChange={() => clearErrors('company.quoting_function_access')}
              />
            </Col>
          )}
        </Row>
      </Card>
      <Styled.Spacer />

      <Card padding="16px 18px 10px 24px">
        <Row>
          <Styled.ImageSection sm={12} md={12} lg={6} xl={6} xxl={6}>
            <Heading3Text>Customise Company</Heading3Text>
            <Styled.Bumper />
            <Subtitle2Text>Upload Logo</Subtitle2Text>
            <FileUploader headerText="Upload Logo" name="whiteLabelling.logo" />
          </Styled.ImageSection>
          <Col sm={12} md={12} lg={12} xl={12} xxl={12}>
            <Styled.Bumper />
            <Subtitle2Text>Change colors</Subtitle2Text>
          </Col>
          <Col sm={12} md={12} lg={3} xl={3} xxl={3}>
            <ColorPicker
              label="Primary Color"
              name="whiteLabelling.primary_color"
              startColor={whiteLabelling?.primary_color}
            />
          </Col>
          <Col sm={12} md={12} lg={3} xl={3} xxl={3}>
            <ColorPicker
              label="Secondary Color"
              name="whiteLabelling.secondary_color"
              startColor={whiteLabelling?.secondary_color}
            />
          </Col>
        </Row>
        <Row>
          <Col sm={12} md={12} lg={12} xl={12} xxl={12}>
            <Subtitle2Text>Add Company Terms & Conditions</Subtitle2Text>
            <Styled.Bumper />
            <RadioButtonGroup
              radioContainerStyle={radioContainerStyle}
              mainContainerStyle={mainContainerStyle}
              options={termsOptions}
              name="whiteLabelling.terms_type"
              registerOptions={{
                required: requiredValidationMessage,
              }}
            />
          </Col>
        </Row>
        {!!terms && (
          <Row>
            {terms === 'text' && (
              <Col sm={12} md={12} lg={6} xl={6} xxl={6}>
                <Input
                  textArea
                  label="Terms and Conditions"
                  disabled={isLoading}
                  placeholder="Type your terms and conditions here"
                  name="whiteLabelling.terms_content"
                  registerOptions={{
                    required: requiredValidationMessage,
                  }}
                />
              </Col>
            )}
            {terms !== 'text' && (
              <Col sm={12} md={12} lg={6} xl={6} xxl={6}>
                <Input
                  label="Link to Terms and Conditions"
                  disabled={isLoading}
                  placeholder="https://www.companywebsite.co.za/terms-of-use/"
                  name="whiteLabelling.terms_content"
                  registerOptions={{
                    required: requiredValidationMessage,
                  }}
                />
              </Col>
            )}
          </Row>
        )}
      </Card>
    </>
  );
};

CompanyForm.defaultProps = {
  isLoading: false,
};

CompanyForm.propTypes = {
  type: PropTypes.string.isRequired,
  isLoading: PropTypes.bool,
};

export default CompanyForm;
